// Columna de filtros de las tablas POS (mismo chasis que Cartera): Búsqueda · Marca · Precio ·
// Financiera · Gama · Capacidad · Con promoción · Solo con stock. Un grupo sin opciones no se pinta.
import type { KeyboardEvent, RefObject } from 'react';
import { Search, X } from 'lucide-react';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { productTierLabel } from '@/shared/lib/format/productTier';
import { cn } from '@/lib/cn';
import { financierChips } from '../lib/financierChips';
import {
    countActivePosFilters,
    DEFAULT_POS_FILTERS,
    toggleValue,
    type PosFilters,
} from '../lib/posFilters';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD =
    'h-[26px] w-full rounded border border-border bg-card px-2 text-[11.5px] text-foreground outline-none focus-visible:border-ring';

export interface PosFilterOptions {
    brands: string[];
    financiers: string[];
    tiers: string[];
    capacities: string[];
}

function ChipGroup({
    label,
    values,
    selected,
    format = (value) => value,
    onToggle,
}: {
    label: string;
    values: string[];
    selected: string[];
    format?: (value: string) => string;
    onToggle: (value: string) => void;
}) {
    if (values.length === 0) return null;
    return (
        <div className={GROUP}>
            <div className={LABEL}>{label}</div>
            <div className="flex flex-wrap gap-1" role="group" aria-label={label}>
                {values.map((value) => {
                    const on = selected.includes(value);
                    return (
                        <button
                            key={value}
                            type="button"
                            aria-pressed={on}
                            onClick={() => {
                                onToggle(value);
                            }}
                            className={cn(
                                'rounded border px-1.5 py-[2px] text-[10.5px] transition-colors',
                                on
                                    ? 'border-primary bg-primary/10 font-semibold text-primary'
                                    : 'border-border text-muted-foreground hover:text-foreground',
                            )}
                        >
                            {format(value)}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function CheckRow({
    label,
    checked,
    onChange,
}: {
    label: string;
    checked: boolean;
    onChange: (next: boolean) => void;
}) {
    return (
        <label className="flex cursor-pointer items-center gap-1.5 py-[3px] text-[11.5px]">
            <input
                type="checkbox"
                checked={checked}
                onChange={(event) => {
                    onChange(event.target.checked);
                }}
                className="size-3.5 accent-primary"
            />
            {label}
        </label>
    );
}

export function PosFiltersPanel({
    filters,
    options,
    onChange,
    onSubmitSearch,
    searchRef,
    searchLabel,
    collapsed,
    onToggleCollapsed,
}: {
    filters: PosFilters;
    options: PosFilterOptions;
    onChange: (next: PosFilters) => void;
    /** Enter en la búsqueda: activa la primera fila. */
    onSubmitSearch: () => void;
    searchRef: RefObject<HTMLInputElement | null>;
    searchLabel: string;
    collapsed: boolean;
    onToggleCollapsed: () => void;
}) {
    const patch = (partial: Partial<PosFilters>) => {
        onChange({ ...filters, ...partial });
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Enter') {
            event.preventDefault();
            onSubmitSearch();
        } else if (event.key === 'Escape') {
            patch({ query: '' });
        }
    };

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={countActivePosFilters(filters)}
            onReset={() => {
                onChange(DEFAULT_POS_FILTERS);
            }}
        >
            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <div className="relative">
                    <Search
                        aria-hidden
                        className="pointer-events-none absolute left-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                        ref={searchRef}
                        type="search"
                        value={filters.query}
                        autoComplete="off"
                        onChange={(event) => {
                            patch({ query: event.target.value });
                        }}
                        onKeyDown={onKeyDown}
                        placeholder="Marca, modelo o SKU"
                        aria-label={searchLabel}
                        className={cn(FIELD, 'pl-6 pr-6 [&::-webkit-search-cancel-button]:hidden')}
                    />
                    {filters.query === '' ? null : (
                        <button
                            type="button"
                            onClick={() => {
                                patch({ query: '' });
                                searchRef.current?.focus();
                            }}
                            aria-label="Limpiar búsqueda"
                            className="absolute right-1 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:text-foreground"
                        >
                            <X className="size-3" aria-hidden />
                        </button>
                    )}
                </div>
            </div>

            <ChipGroup
                label="Marca"
                values={options.brands}
                selected={filters.brands}
                onToggle={(value) => {
                    patch({ brands: toggleValue(filters.brands, value) });
                }}
            />

            <div className={GROUP}>
                <div className={LABEL}>Precio</div>
                <div className="grid grid-cols-2 gap-1">
                    <input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        value={filters.minPrice}
                        onChange={(event) => {
                            patch({ minPrice: event.target.value });
                        }}
                        placeholder="Mín"
                        aria-label="Precio mínimo"
                        className={cn(FIELD, 'tabular-nums')}
                    />
                    <input
                        type="number"
                        min={0}
                        inputMode="numeric"
                        value={filters.maxPrice}
                        onChange={(event) => {
                            patch({ maxPrice: event.target.value });
                        }}
                        placeholder="Máx"
                        aria-label="Precio máximo"
                        className={cn(FIELD, 'tabular-nums')}
                    />
                </div>
            </div>

            {options.financiers.length === 0 ? null : (
                <div className={GROUP}>
                    <div className={LABEL}>Financiera</div>
                    <select
                        value={filters.financier}
                        aria-label="Financiera"
                        onChange={(event) => {
                            patch({ financier: event.target.value });
                        }}
                        className={FIELD}
                    >
                        <option value="">Cualquiera</option>
                        {financierChips(options.financiers).map((chip) => (
                            <option key={chip.code} value={chip.code}>
                                {chip.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <ChipGroup
                label="Gama"
                values={options.tiers}
                selected={filters.tiers}
                format={productTierLabel}
                onToggle={(value) => {
                    patch({ tiers: toggleValue(filters.tiers, value) });
                }}
            />

            <ChipGroup
                label="Capacidad"
                values={options.capacities}
                selected={filters.capacities}
                onToggle={(value) => {
                    patch({ capacities: toggleValue(filters.capacities, value) });
                }}
            />

            <div className={GROUP}>
                <div className={LABEL}>Disponibilidad</div>
                <CheckRow
                    label="Con promoción"
                    checked={filters.promoOnly}
                    onChange={(promoOnly) => {
                        patch({ promoOnly });
                    }}
                />
                <CheckRow
                    label="Solo con stock"
                    checked={filters.inStockOnly}
                    onChange={(inStockOnly) => {
                        patch({ inStockOnly });
                    }}
                />
            </div>
        </FiltersColumn>
    );
}
